/**
 * Utilitário para máscara e validação de CNPJ no frontend
 * Suporta digitação, colagem e validação em tempo real
 */

/**
 * Remove todos os caracteres não numéricos
 * @param {string} value - Valor a ser limpo
 * @returns {string} - Apenas números
 */
export const cleanCNPJ = (value) => {
  if (!value) return '';
  return value.toString().replace(/[^\d]/g, '');
};

/**
 * Aplica máscara de CNPJ: XX.XXX.XXX/XXXX-XX
 * @param {string} value - Valor a ser formatado
 * @returns {string} - Valor com máscara aplicada
 */
export const maskCNPJ = (value) => {
  const cleaned = cleanCNPJ(value);
  
  // Aplicar máscara progressivamente conforme o usuário digita
  if (cleaned.length <= 2) {
    return cleaned;
  } else if (cleaned.length <= 5) {
    return cleaned.replace(/^(\d{2})(\d{0,3})/, '$1.$2');
  } else if (cleaned.length <= 8) {
    return cleaned.replace(/^(\d{2})(\d{3})(\d{0,3})/, '$1.$2.$3');
  } else if (cleaned.length <= 12) {
    return cleaned.replace(/^(\d{2})(\d{3})(\d{3})(\d{0,4})/, '$1.$2.$3/$4');
  } else {
    return cleaned.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{0,2})/, '$1.$2.$3/$4-$5');
  }
};

/**
 * Handler para input de CNPJ - aplica máscara durante digitação
 * @param {Event} event - Evento de input
 * @returns {string} - Valor formatado
 */
export const handleCNPJInput = (event) => {
  const input = event.target;
  const cursorPosition = input.selectionStart;
  const oldValue = input.value;
  const oldLength = oldValue.length;
  
  // Aplicar máscara
  const newValue = maskCNPJ(oldValue);
  const newLength = newValue.length;
  
  // Ajustar posição do cursor
  const lengthDiff = newLength - oldLength;
  const newCursorPosition = cursorPosition + lengthDiff;
  
  // Atualizar valor
  input.value = newValue;
  
  // Restaurar posição do cursor
  input.setSelectionRange(newCursorPosition, newCursorPosition);
  
  return newValue;
};

/**
 * Handler para paste de CNPJ - aceita apenas números
 * @param {ClipboardEvent} event - Evento de paste
 * @returns {string} - Valor formatado
 */
export const handleCNPJPaste = (event) => {
  event.preventDefault();
  
  // Obter texto colado
  const pastedText = event.clipboardData.getData('text');
  
  // Limpar e formatar
  const cleaned = cleanCNPJ(pastedText);
  const formatted = maskCNPJ(cleaned);
  
  // Inserir no input
  const input = event.target;
  input.value = formatted;
  
  // Disparar evento de input para atualizar React state
  const inputEvent = new Event('input', { bubbles: true });
  input.dispatchEvent(inputEvent);
  
  return formatted;
};

/**
 * Valida se o CNPJ tem 14 dígitos
 * @param {string} cnpj - CNPJ para validar
 * @returns {boolean} - true se tem 14 dígitos
 */
export const isValidLength = (cnpj) => {
  const cleaned = cleanCNPJ(cnpj);
  return cleaned.length === 14;
};

/**
 * Valida se todos os dígitos são iguais (CNPJ inválido)
 * @param {string} cnpj - CNPJ para validar
 * @returns {boolean} - true se todos os dígitos são iguais
 */
export const hasAllSameDigits = (cnpj) => {
  const cleaned = cleanCNPJ(cnpj);
  return /^(\d)\1{13}$/.test(cleaned);
};

/**
 * Calcula um dígito verificador do CNPJ
 * @param {string} base - Base numérica
 * @param {number[]} weights - Pesos para multiplicação
 * @returns {number} - Dígito verificador
 */
const calculateDigit = (base, weights) => {
  let sum = 0;
  
  for (let i = 0; i < base.length; i++) {
    sum += parseInt(base[i]) * weights[i];
  }
  
  const remainder = sum % 11;
  return remainder < 2 ? 0 : 11 - remainder;
};

/**
 * Valida os dígitos verificadores do CNPJ
 * @param {string} cnpj - CNPJ para validar
 * @returns {boolean} - true se dígitos verificadores estão corretos
 */
export const validateCheckDigits = (cnpj) => {
  const cleaned = cleanCNPJ(cnpj);
  
  if (cleaned.length !== 14) return false;

  // Extrair os 12 primeiros dígitos e os 2 dígitos verificadores
  const base = cleaned.substring(0, 12);
  const providedDigits = cleaned.substring(12, 14);
  
  // Calcular primeiro dígito verificador
  const firstDigit = calculateDigit(base, [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]);
  
  // Calcular segundo dígito verificador
  const secondDigit = calculateDigit(base + firstDigit, [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]);
  
  const calculatedDigits = `${firstDigit}${secondDigit}`;
  
  return calculatedDigits === providedDigits;
};

/**
 * Validação completa de CNPJ
 * @param {string} cnpj - CNPJ para validar
 * @param {boolean} validateDigits - Se deve validar dígitos verificadores (padrão: true)
 * @returns {object} - { valid: boolean, error: string }
 */
export const validateCNPJ = (cnpj, validateDigits = true) => {
  // Verificar se foi fornecido
  if (!cnpj || cnpj.trim() === '') {
    return {
      valid: false,
      error: 'CNPJ é obrigatório'
    };
  }

  const cleaned = cleanCNPJ(cnpj);

  // Verificar comprimento
  if (cleaned.length !== 14) {
    return {
      valid: false,
      error: `CNPJ deve ter 14 dígitos (${cleaned.length} informados)`
    };
  }

  // Verificar se todos os dígitos são iguais
  if (hasAllSameDigits(cnpj)) {
    return {
      valid: false,
      error: 'CNPJ inválido: todos os dígitos são iguais'
    };
  }

  // Validar dígitos verificadores (se solicitado)
  if (validateDigits && !validateCheckDigits(cnpj)) {
    return {
      valid: false,
      error: 'CNPJ inválido: dígitos verificadores incorretos'
    };
  }

  return {
    valid: true,
    error: null
  };
};

/**
 * Hook personalizado para React - gerencia estado e validação de CNPJ
 * Nota: Este hook requer React importado no componente que o usa
 * @param {string} initialValue - Valor inicial
 * @returns {object} - { value, formatted, error, onChange, onPaste, isValid }
 */
export const useCNPJInput = (initialValue = '', React) => {
  if (!React) {
    throw new Error('useCNPJInput requer React como segundo parâmetro');
  }
  
  const [value, setValue] = React.useState(initialValue);
  const [error, setError] = React.useState(null);

  const handleChange = (event) => {
    const newValue = maskCNPJ(event.target.value);
    setValue(newValue);
    
    // Validar apenas se tiver 14 dígitos
    const cleaned = cleanCNPJ(newValue);
    if (cleaned.length === 14) {
      const validation = validateCNPJ(newValue, false); // Não validar dígitos durante digitação
      setError(validation.error);
    } else {
      setError(null);
    }
  };

  const handlePaste = (event) => {
    event.preventDefault();
    const pastedText = event.clipboardData.getData('text');
    const formatted = maskCNPJ(pastedText);
    setValue(formatted);
    
    // Validar após colar
    const validation = validateCNPJ(formatted, false);
    setError(validation.error);
  };

  const isValid = () => {
    const validation = validateCNPJ(value, false);
    return validation.valid;
  };

  return {
    value,
    formatted: value,
    cleaned: cleanCNPJ(value),
    error,
    onChange: handleChange,
    onPaste: handlePaste,
    isValid: isValid(),
    setValue
  };
};

// Exportar tudo como default também
export default {
  cleanCNPJ,
  maskCNPJ,
  handleCNPJInput,
  handleCNPJPaste,
  isValidLength,
  hasAllSameDigits,
  validateCheckDigits,
  validateCNPJ,
  useCNPJInput
};
